import { useEffect, useRef, useState } from 'react';

import {
  ListUsersPaginationQuery,
  User,
  useListUsersPaginationQuery,
} from '@graphql/generated';

const DEFAULT_PER_PAGE = 10;

interface UseUserPaginationOptions {
  searchTerm: string;
  perPage?: number;
  fetchPolicy?: 'cache-and-network' | 'network-only';
  skip?: boolean;
}

/**
 * Manages cursor-based user pagination with deduplication.
 * Used by NewChat and NewChatModal for user search with infinite scroll.
 */
export const useUserPagination = ({
  searchTerm,
  perPage = DEFAULT_PER_PAGE,
  fetchPolicy = 'network-only',
  skip = false,
}: UseUserPaginationOptions) => {
  const [refreshing, setRefreshing] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null | undefined>(null);
  const [users, setUsers] = useState<User[]>([]);

  const afterComplete = (
    isNew: boolean,
    data: ListUsersPaginationQuery | undefined
  ) => {
    const edges = data?.listUsersPagination?.edges;
    if (edges) {
      const newEdges = edges.filter((edge) => edge?.node);
      const newUsers = newEdges?.map((edge) => edge?.node as User);

      if (isNew) {
        setUsers(newUsers);
      } else {
        setUsers((prev) => {
          const uniqueUsers = [
            ...prev.filter(
              (user) => !newUsers?.some((newUser) => newUser?.id === user.id)
            ),
            ...newUsers,
          ];
          return uniqueUsers;
        });
      }
    }
    const pageInfo = data?.listUsersPagination?.pageInfo;
    if (pageInfo?.hasNextPage) {
      setHasNextPage(pageInfo.hasNextPage);
      setNextCursor(pageInfo.endCursor);
    } else {
      setHasNextPage(false);
      setNextCursor(null);
    }
  };

  const afterCompleteRef = useRef(afterComplete);
  afterCompleteRef.current = afterComplete;

  // Track the last queryData object we've applied so a fresh Apollo response
  // — whether from the initial load, a term change, or a manual refetch()
  // call (see `refresh` below) — is applied exactly once. Guarding on
  // searchTerm instead (as this used to) breaks pull-to-refresh: refetch()
  // reuses the same term, so a term-keyed guard would treat the refreshed
  // response as already-applied and never update `users` — TA-10166.
  const lastAppliedDataRef = useRef<ListUsersPaginationQuery | undefined>(
    undefined
  );

  const {
    data: queryData,
    fetchMore: loadMore,
    loading,
    refetch,
  } = useListUsersPaginationQuery({
    variables: { term: searchTerm, first: perPage },
    skip: !searchTerm || skip,
    fetchPolicy,
  });

  useEffect(() => {
    // searchTerm empty means the query is skipped — Apollo can still be
    // holding the previous non-empty term's `data`, so bail out here rather
    // than reapplying it (the effect below is the one that clears state for
    // the empty-searchTerm case) — TA-10166.
    if (!searchTerm) return;
    // This effect also re-runs on every searchTerm change, before Apollo's
    // response for the new variables has arrived — at that point queryData
    // is still the PREVIOUS term's (already-applied) data object, so the
    // identity guard below would otherwise skip it as "unchanged" even
    // though it doesn't correspond to the new searchTerm at all. Wait for
    // `loading` to clear so queryData is guaranteed to match searchTerm
    // before the identity check runs (TA-10166).
    if (loading) return;
    if (!queryData) return;
    if (lastAppliedDataRef.current === queryData) return;
    lastAppliedDataRef.current = queryData;
    afterCompleteRef.current(true, queryData);
  }, [queryData, searchTerm, loading]);

  // The query is skipped once searchTerm is cleared (e.g. it dropped below
  // a caller's min-char gate), but Apollo doesn't clear `data` on its own —
  // without this, `users` keeps showing the last above-threshold search's
  // results indefinitely (TA-10166).
  useEffect(() => {
    if (searchTerm) return;
    lastAppliedDataRef.current = undefined;
    setUsers([]);
    setHasNextPage(false);
    setNextCursor(null);
  }, [searchTerm]);

  const refresh = () => {
    setRefreshing(true);
    refetch({
      term: searchTerm,
      first: users?.length > perPage ? users?.length : perPage,
    }).finally(() => setRefreshing(false));
  };

  const loadMoreUsers = () => {
    if (nextCursor && hasNextPage) {
      loadMore({
        variables: {
          term: searchTerm,
          first: perPage,
          after: nextCursor,
        },
      }).then((res) => {
        afterComplete(false, res?.data);
      });
    }
  };

  return {
    users,
    loading,
    refreshing,
    hasNextPage,
    refresh,
    loadMoreUsers,
  };
};
